import type { MeetingLanguage, MeetingParticipant, VoiceCast, VoiceCastAssignment, VoicePresentation } from '../types';

export type VoiceProfile = {
  id: string;
  language: MeetingLanguage;
  name: string;
  description: string;
  presentation: VoicePresentation;
};

export const voiceProfiles: VoiceProfile[] = [
  { id: 'en-jenny', language: 'English', name: 'Jenny', description: 'Warm and conversational', presentation: 'feminine' },
  { id: 'en-ava', language: 'English', name: 'Ava', description: 'Clear and composed', presentation: 'feminine' },
  { id: 'en-guy', language: 'English', name: 'Guy', description: 'Steady and direct', presentation: 'masculine' },
  { id: 'en-andrew', language: 'English', name: 'Andrew', description: 'Measured and thoughtful', presentation: 'masculine' },
  { id: 'ja-nanami', language: 'Japanese', name: 'Nanami', description: 'Warm and conversational', presentation: 'feminine' },
  { id: 'ja-shiori', language: 'Japanese', name: 'Shiori', description: 'Calm and composed', presentation: 'feminine' },
  { id: 'ja-keita', language: 'Japanese', name: 'Keita', description: 'Steady and direct', presentation: 'masculine' },
  { id: 'ja-naoki', language: 'Japanese', name: 'Naoki', description: 'Measured and thoughtful', presentation: 'masculine' },
];

export function voicesForLanguage(language: MeetingLanguage) {
  return voiceProfiles.filter((voice) => voice.language === language);
}

export function voicePreviewUrl(voiceProfileId: string) {
  return `/voice-previews/${encodeURIComponent(voiceProfileId)}.wav`;
}

export type VoiceSelections = Record<string, string>;

export function savedVoiceSelections(
  roster: MeetingParticipant[],
  language: MeetingLanguage,
  previous?: VoiceCast | null,
): VoiceSelections {
  const voices = voicesForLanguage(language);
  const allowed = new Set(voices.map((voice) => voice.id));
  const feminine = voices.filter((voice) => voice.presentation === 'feminine');
  const masculine = voices.filter((voice) => voice.presentation === 'masculine');
  const defaults = Array.from({ length: Math.max(feminine.length, masculine.length) }, (_, index) => [feminine[index], masculine[index]]).flat().filter(Boolean) as VoiceProfile[];
  const byId = new Map((previous?.assignments || []).map((assignment) => [assignment.participantId, assignment]));
  const byName = new Map((previous?.assignments || []).map((assignment) => [assignment.name.trim().toLocaleLowerCase(), assignment]));
  return Object.fromEntries(roster.filter((participant) => !participant.isUser).slice(0, 12).map((participant, index) => {
    const saved = byId.get(participant.id) || byName.get(participant.name.trim().toLocaleLowerCase());
    return [participant.id, saved && allowed.has(saved.voiceProfileId) ? saved.voiceProfileId : defaults[index % defaults.length]?.id || voices[0]?.id || ''];
  }));
}

export function voiceCastFromSelections(roster: MeetingParticipant[], language: MeetingLanguage, selections: VoiceSelections): VoiceCast | null {
  const simulated = roster.filter((participant) => !participant.isUser).slice(0, 12);
  const voices = voicesForLanguage(language);
  if (!simulated.length) return null;
  const assignments: VoiceCastAssignment[] = [];
  for (const participant of simulated) {
    const voice = voices.find((profile) => profile.id === selections[participant.id]);
    if (!voice) return null;
    assignments.push({
      participantId: participant.id,
      name: participant.name,
      role: participant.role,
      voiceProfileId: voice.id,
      presentation: voice.presentation,
    });
  }
  return { version: 1, assignments };
}
